import type { Room, RemoteParticipant } from 'livekit-client'
import { encodePacket, decodePacket, isReliable, type ControlPacket } from './protocol'

/**
 * Publish one control packet to a single destination identity. Mouse-move is
 * lossy (drop stale); everything else is reliable/ordered. This is the only
 * remote-control call site of room.localParticipant.publishData (kept here so
 * the data-channel usage for control stays centralized and testable).
 */
export async function publishControl(
  room: Pick<Room, 'localParticipant'>,
  targetIdentity: string,
  sessionId: string,
  packet: ControlPacket,
): Promise<void> {
  const data = encodePacket(sessionId, packet)
  await room.localParticipant.publishData(data, {
    reliable: isReliable(packet),
    destinationIdentities: [targetIdentity],
  })
}

export type OnControlPacket = (senderIdentity: string, sessionId: string, packet: ControlPacket) => void

/**
 * Build a RoomEvent.DataReceived listener that decodes the envelope and hands
 * (senderIdentity, sessionId, packet) to onPacket. Drops anything that can't be
 * decoded or whose sender can't be identified — the consumer enforces the
 * active-session + approved-controller gate on top of this.
 */
export function makeDataReceivedHandler(onPacket: OnControlPacket) {
  return (payload: Uint8Array, participant?: RemoteParticipant) => {
    if (!participant?.identity) return
    const decoded = decodePacket(payload)
    if (!decoded) return
    onPacket(participant.identity, decoded.sessionId, decoded.packet)
  }
}
